import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { apiService } from '../../services/api';

const GREEN = '#2e7d32';
const BLUE = '#1565c0';

interface Bucket {
  gross: number;
  providerNet: number;
  visits: number;
}

type ItemKind = 'visit' | 'contract_payment' | 'monthly_installment' | 'one_time';

interface ForecastItem {
  date: string;
  kind: ItemKind;
  serviceRequestId: string;
  customerName: string;
  address: string;
  serviceLabel: string;
  amount: number;
  providerNet: number;
}

export interface RevenueForecastData {
  from: string;
  to: string;
  days: number;
  totals: Bucket;
  horizons: Array<Bucket & { days: number }>;
  weekly: Array<Bucket & { weekStart: string }>;
  byService: Array<Bucket & { category: string; label: string }>;
  byCustomer: Array<Bucket & { customerId: string | null; customerName: string; address: string }>;
  items: ForecastItem[];
  itemsTruncated: boolean;
  overdue: { count: number; gross: number };
  needsScheduling: { count: number; grossPerVisit: number };
  onDemand: { count: number };
  onHold: { count: number };
  missingPrice: { count: number };
  missingPaymentSchedule: { count: number };
  pipeline: {
    estimates: { count: number; gross: number };
    renewals: { count: number; gross: number };
    invitations: { count: number; quoted: number };
  };
  skipHistoryStartedAt: string | null;
}

const money = (n: number | undefined) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

const KIND_LABEL: Record<ItemKind, string> = {
  visit: 'Visit',
  contract_payment: 'Contract payment',
  monthly_installment: 'Monthly installment',
  one_time: 'One-time job',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const ItemAmount: React.FC<{ item: ForecastItem }> = ({ item }) =>
  item.amount > 0 ? (
    <>{money(item.amount)}</>
  ) : (
    <Typography component="span" variant="body2" color="text.secondary">
      Covered by contract
    </Typography>
  );

const RevenueForecastTab: React.FC = () => {
  const [data, setData] = useState<RevenueForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [horizon, setHorizon] = useState(30);
  const [showAllVisits, setShowAllVisits] = useState(false);
  const [showAllCustomers, setShowAllCustomers] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.get<RevenueForecastData>('/provider-financial/forecast', { days: 90 });
      if (res.success && res.data) setData(res.data);
      else throw new Error(res.error || 'Failed to load forecast');
    } catch (err: any) {
      setError(err.message || 'Failed to load forecast');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const horizonEnd = useMemo(() => {
    if (!data) return null;
    return new Date(new Date(data.from).getTime() + horizon * 24 * 60 * 60 * 1000);
  }, [data, horizon]);

  const visibleItems = useMemo(
    () => (data && horizonEnd ? data.items.filter((i) => new Date(i.date) < horizonEnd) : []),
    [data, horizonEnd]
  );

  const chartData = useMemo(
    () =>
      (data?.weekly || [])
        .filter((w) => !horizonEnd || new Date(w.weekStart) < horizonEnd)
        .map((w) => ({ week: shortDate(w.weekStart), gross: w.gross, net: w.providerNet })),
    [data, horizonEnd]
  );

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const current = data.horizons.find((h) => h.days === horizon) || data.totals;
  const notices: string[] = [];
  if (data.overdue.count > 0) {
    notices.push(
      `${plural(data.overdue.count, 'visit')} past due and not completed (${money(data.overdue.gross)}). They're not in the totals above; complete, skip, or reschedule them on My Dashboard.`
    );
  }
  if (data.needsScheduling.count > 0) {
    notices.push(
      `${plural(data.needsScheduling.count, 'accepted service')} still ${data.needsScheduling.count === 1 ? 'needs' : 'need'} a first date, so ${data.needsScheduling.count === 1 ? 'it isn\u2019t' : 'they aren\u2019t'} counted yet.`
    );
  }
  if (data.onDemand.count > 0) {
    notices.push(`${plural(data.onDemand.count, 'on-demand service')} (like snow removal) can't be predicted and ${data.onDemand.count === 1 ? 'is' : 'are'} left out.`);
  }
  if (data.onHold.count > 0) {
    notices.push(`${plural(data.onHold.count, 'service')} for customers on hold ${data.onHold.count === 1 ? 'is' : 'are'} left out.`);
  }
  if (data.missingPrice.count > 0) {
    notices.push(`${plural(data.missingPrice.count, 'service')} ${data.missingPrice.count === 1 ? 'has' : 'have'} no price on file and ${data.missingPrice.count === 1 ? 'is' : 'are'} left out.`);
  }
  if (data.missingPaymentSchedule.count > 0) {
    notices.push(`${plural(data.missingPaymentSchedule.count, 'seasonal contract')} ${data.missingPaymentSchedule.count === 1 ? 'has' : 'have'} no payment schedule on file, so no payment is counted.`);
  }

  const itemsToShow = showAllVisits ? visibleItems : visibleItems.slice(0, 25);
  const customersToShow = showAllCustomers ? data.byCustomer : data.byCustomer.slice(0, 10);
  const pipelineTotal = data.pipeline.estimates.count + data.pipeline.renewals.count + data.pipeline.invitations.count;

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          What your active services are scheduled to bring in, based on each customer&apos;s schedule and price. Amounts are service prices before sales tax.
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={horizon}
          onChange={(_, v) => v && setHorizon(v)}
          aria-label="Forecast window"
        >
          {data.horizons.map((h) => (
            <ToggleButton key={h.days} value={h.days}>
              Next {h.days} days
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Booked revenue</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: GREEN }}>{money(current.gross)}</Typography>
              <Typography variant="caption" color="text.secondary">Next {horizon} days</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Your earnings after LawnHub fee</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: BLUE }}>{money(current.providerNet)}</Typography>
              <Typography variant="caption" color="text.secondary">Before your own expenses</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Scheduled visits</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{current.visits}</Typography>
              <Typography variant="caption" color="text.secondary">Includes visits already paid for by a seasonal contract</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: 340 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          By week
        </Typography>
        <ResponsiveContainer width="100%" height="88%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
            <Tooltip formatter={(v) => money(Number(v ?? 0))} />
            <Legend />
            <Bar dataKey="gross" name="Booked revenue" fill={GREEN} />
            <Bar dataKey="net" name="After LawnHub fee" fill={BLUE} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {notices.length > 0 && (
        <Alert severity="info">
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
            {notices.map((n) => (
              <li key={n}>
                <Typography variant="body2">{n}</Typography>
              </li>
            ))}
          </Stack>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography fontWeight={600}>By service (next {data.days} days)</Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {data.byService.length === 0 ? (
                  <TableRow>
                    <TableCell>No scheduled services</TableCell>
                  </TableRow>
                ) : (
                  data.byService.map((s) => (
                    <TableRow key={s.category}>
                      <TableCell>{s.label}</TableCell>
                      <TableCell align="right">{plural(s.visits, 'visit')}</TableCell>
                      <TableCell align="right">{money(s.gross)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              <Typography fontWeight={600}>By customer (next {data.days} days)</Typography>
            </Box>
            <Table size="small">
              <TableBody>
                {customersToShow.length === 0 ? (
                  <TableRow>
                    <TableCell>No scheduled services</TableCell>
                  </TableRow>
                ) : (
                  customersToShow.map((c) => (
                    <TableRow key={`${c.customerId}|${c.address}`}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{c.customerName}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.address}</Typography>
                      </TableCell>
                      <TableCell align="right">{plural(c.visits, 'visit')}</TableCell>
                      <TableCell align="right">{money(c.gross)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {data.byCustomer.length > 10 && (
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Button size="small" onClick={() => setShowAllCustomers((v) => !v)}>
                  {showAllCustomers ? 'Show top 10' : `Show all ${data.byCustomer.length}`}
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography fontWeight={600}>Upcoming (next {horizon} days)</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsToShow.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">Nothing scheduled in this window.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                itemsToShow.map((item) => (
                  <TableRow key={`${item.serviceRequestId}-${item.date}-${item.kind}`}>
                    <TableCell>{shortDate(item.date)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.customerName}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.address}</Typography>
                    </TableCell>
                    <TableCell>{item.serviceLabel}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={KIND_LABEL[item.kind]} />
                    </TableCell>
                    <TableCell align="right">
                      <ItemAmount item={item} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {visibleItems.length > 25 && (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Button size="small" onClick={() => setShowAllVisits((v) => !v)}>
              {showAllVisits ? 'Show fewer' : `Show all ${visibleItems.length}`}
            </Button>
          </Box>
        )}
      </Paper>

      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Pipeline — not booked yet
        </Typography>
        {pipelineTotal === 0 ? (
          <Typography variant="body2" color="text.secondary">No open estimates, renewal offers, or pending invitations.</Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Open Additional Services estimates</Typography>
                  <Typography variant="h6" fontWeight={700}>{money(data.pipeline.estimates.gross)}</Typography>
                  <Typography variant="caption" color="text.secondary">{plural(data.pipeline.estimates.count, 'estimate')} waiting on the customer</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Renewal offers</Typography>
                  <Typography variant="h6" fontWeight={700}>{money(data.pipeline.renewals.gross)}</Typography>
                  <Typography variant="caption" color="text.secondary">{plural(data.pipeline.renewals.count, 'offer')} awaiting a decision (per visit or contract price)</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">Invited customers not signed up yet</Typography>
                  <Typography variant="h6" fontWeight={700}>{money(data.pipeline.invitations.quoted)}</Typography>
                  <Typography variant="caption" color="text.secondary">{plural(data.pipeline.invitations.count, 'customer')} (quoted per visit or contract price)</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        The forecast assumes every scheduled visit happens. Skips, weather delays and cancellations after today aren&apos;t predicted.
        {data.skipHistoryStartedAt
          ? ` Skipped visits have been recorded since ${shortDate(data.skipHistoryStartedAt)}.`
          : ' Skipped visits are recorded from now on.'}
      </Typography>
    </Stack>
  );
};

export default RevenueForecastTab;
